/*
 * Copyright 2025 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import { useMediaQuery, useTheme } from "@material-ui/core";
import React, { ReactElement, ReactNode, useEffect, useRef } from "react";
import { ListChildComponentProps, VariableSizeList } from "react-window";

const LISTBOX_PADDING = 8; // px
const MAX_VISIBLE_ITEMS = 8;

function renderRow(props: ListChildComponentProps): ReactElement {
    const { data, index, style } = props;

    return React.cloneElement(data[index], {
        style: {
            ...style,
            top:
                (typeof style.top === "number"
                    ? style.top
                    : parseInt(String(style.top), 10)) + LISTBOX_PADDING,
        },
    });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);

    return <div ref={ref} {...props} {...outerProps} />;
});

OuterElementType.displayName = "OuterElementType";

function useResetCache(data: ReactNode[]): React.RefObject<VariableSizeList> {
    const ref = useRef<VariableSizeList>(null);
    useEffect(() => {
        if (ref.current != null) {
            ref.current.resetAfterIndex(0, true);
        }
    }, [data]);

    return ref;
}

interface ListboxComponentProps extends React.HTMLAttributes<HTMLElement> {
    children?: ReactNode;
}

export const AutocompleteVirtualisedListbox = React.forwardRef<
    HTMLDivElement,
    ListboxComponentProps
>(function AutocompleteVirtualisedListbox(props, ref) {
    // eslint-disable-next-line react/prop-types
    const { children, ...other } = props;
    const itemData = React.Children.toArray(children);
    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up("sm"), { noSsr: true });
    const itemCount = itemData.length;
    const itemSize = smUp ? 48 : 50;

    const getChildSize = (): number => {
        return itemSize;
    };

    const getHeight = (): number => {
        if (itemCount > MAX_VISIBLE_ITEMS) {
            return MAX_VISIBLE_ITEMS * itemSize;
        }

        return itemCount * itemSize;
    };

    const gridRef = useResetCache(itemData);

    return (
        <div ref={ref}>
            <OuterElementContext.Provider value={other}>
                <VariableSizeList
                    height={getHeight() + 2 * LISTBOX_PADDING}
                    innerElementType="ul"
                    itemCount={itemCount}
                    itemData={itemData}
                    itemSize={() => getChildSize()}
                    outerElementType={OuterElementType}
                    overscanCount={5}
                    ref={gridRef}
                    width="100%"
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});

AutocompleteVirtualisedListbox.displayName = "AutocompleteVirtualisedListbox";
